import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react'

import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import toast from 'react-hot-toast'
import Joi from 'joi'

import { SingleSelect, MultiSelect } from '../../../components/Select'
import { StandardInput, DatepickerInput } from '../../../components/Input'

import { getFromLocalStorage } from '../../../utils/util'

import apiClient from '../../../helpers/apiClient'

const Others = forwardRef((props, ref) => {
  const history = useHistory()

  const [startAvailability, setStartAvailability] = useState(new Date())
  const [shiftPreference, setShiftPreference] = useState({})
  const [licenseAndCert, setLicenseAndCert] = useState('')
  const [software, setSoftware] = useState([])
  const [education, setEducation] = useState([])
  const [language, setLanguage] = useState([])
  const [fieldErrors, setFieldErrors] = useState({})

  const [shiftList, setShiftList] = useState([])
  const [softwareList, setSoftwareList] = useState([])
  const [languageList, setLanguageList] = useState([])

  const [educationList, setEducationList] = useState([])

  const validationSchema = useMemo(() => {
    return Joi.object({
      startAvailability: Joi.date()
        .min('now')
        .label('Start Availability')
        .required()
        .messages({
          'date.min': '{{#label}} must not be a past date',
          'any.required': '{{#label}} is required',
          'date.base': '{{#label}} must be a valid date',
        }),
      shiftPreference: Joi.string().label('Shift Preference').required(),
      licenseAndCert: Joi.string().label('License/Certification').required(),
      software: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Software')
        .required()
        .messages({
          'array.min': 'Please select at least one software.',
          'any.required': 'Personal Skill selection is required.',
        }),
      education: Joi.string().label('Education').required(),
      language: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Language')
        .required()
        .messages({
          'array.min': 'Please select at least one language.',
          'any.required': 'Language selection is required.',
        }),
    })
  }, [])

  useEffect(() => {
    const others = getFromLocalStorage('others')

    setStartAvailability(others?.startAvailability || new Date())
    setShiftPreference(others?.shiftPreference || '')
    setEducation(others?.education || '')
    setLanguage((others && JSON.parse(others?.language)) || [])
    setLicenseAndCert(others?.licenseAndCert)
    setSoftware((others && JSON.parse(others?.software)) || [])

    setEducationList([
      { value: 'No preference', label: 'No preference' },
      { value: 'Undergraduate', label: 'Undergraduate' },
      { value: 'Bachelors Degree', label: 'Bachelors Degree' },
      { value: 'Masters Degree', label: 'Masters Degree' },
    ])

    apiClient.get('/software').then((resp) => {
      const result = resp?.data?.resp
      const mappedResult = result.map((e) => ({
        value: e.software,
        label: e.software,
      }))
      setSoftwareList(mappedResult)
    })

    apiClient.get('/language').then((resp) => {
      const result = resp?.data?.resp
      const mappedResult = result.map((e) => ({
        value: e.name,
        label: e.name,
      }))

      setLanguageList(mappedResult)
    })

    setShiftList([
      { value: 'Morning Shift - PH Time', label: 'Morning Shift - PH Time' },
      {
        value: 'Afternoon Shift - PH Time',
        label: 'Afternoon Shift - PH Time',
      },
      {
        value: 'Night Shift - PH Time',
        label: 'Night Shift - PH Time',
      },
      {
        value: 'Rotational Shift - PH Time',
        label: 'Rotational Shift - PH Time',
      },
    ])
  }, [])

  const handleSubmit = async () => {
    const formData = {
      startAvailability,
      shiftPreference: JSON.stringify(shiftPreference),
      language: JSON.stringify(language),
      licenseAndCert,
      education: JSON.stringify(education),
      software: JSON.stringify(software),
    }

    const forValidation = {
      startAvailability,
      shiftPreference: shiftPreference?.label,
      language,
      licenseAndCert,
      education: education?.label,
      software,
    }

    const result = validationSchema.validate(forValidation, {
      abortEarly: false,
    })

    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)

      toast.error('Please fix the validation errors to continue.')

      return
    }

    localStorage.setItem('others', JSON.stringify(formData))

    const requestorDetails = getFromLocalStorage('requestorDetails')
    const jobDetails = getFromLocalStorage('jobDetails')
    const paymentAndBenefits = getFromLocalStorage('paymentAndBenefits')
    const qualifications = getFromLocalStorage('qualifications')
    const others = getFromLocalStorage('others')

    const convertArrayOfObjectsIntoArray = (arrObj) => {
      const parsed = JSON.parse(arrObj)
      return JSON.stringify(parsed.map((e) => e.value))
    }

    let formattedExperiences =
      qualifications && JSON.parse(qualifications?.specialization)
    formattedExperiences = JSON.stringify(
      formattedExperiences.map((e) => ({
        years: qualifications?.years?.value,
        specialization: e.value,
      }))
    )

    const payload = {
      job_request: {
        company: requestorDetails?.company,
        company_url: requestorDetails?.companyUrl,
        email: requestorDetails?.email,
        location: requestorDetails?.location,
        requestor_name: requestorDetails?.requestorName,
      },
      job: {
        // Job Details
        title: jobDetails?.jobTitle,
        description: jobDetails?.jobDesc,
        job_type: jobDetails?.engagementType,
        industry_id: jobDetails?.industry?.value,
        requires_coaching: jobDetails?.requiresCoaching,
        time_shift: jobDetails?.shift?.value,
        active_vacancy: jobDetails?.activeVacancy?.toString(),
        vacancy_for_pooling: jobDetails?.vacancyPooling?.toString(),
        countries: jobDetails?.country,

        // Payment and Benefits
        benefits: convertArrayOfObjectsIntoArray(paymentAndBenefits?.benefits),
        currencies: paymentAndBenefits?.currency?.value,
        max_salary: paymentAndBenefits?.maxPayment,
        min_salary: paymentAndBenefits?.minPayment,
        salary_basis: paymentAndBenefits?.payBasis,

        // Qualifications
        experience: formattedExperiences,
        technical_skills: qualifications?.techSkills,
        hardware_requirement: convertArrayOfObjectsIntoArray(
          qualifications?.hardwareRequirements
        ),
        personal_skills: qualifications?.techSkills,

        // Others
        education: others?.education && JSON.parse(others?.education)?.value,
        language: others?.language,
        shift_availability:
          others?.shiftPreference && JSON.parse(others?.shiftPreference)?.value,
        certification: others?.licenseAndCert,
        software: others?.software,
        availability_to_start: others?.startAvailability,
        second_interview_questions: '[]', // new field in job posting in which recruiters set questions to ask applicants in interview

        // Default
        status: 'draft',
        priority: false,
      },
    }

    await apiClient.post('/job-request', payload)

    localStorage.removeItem('requestorDetails')
    localStorage.removeItem('jobDetails')
    localStorage.removeItem('paymentAndBenefits')
    localStorage.removeItem('qualifications')
    localStorage.removeItem('others')

    history.push(`/vacancy-request/success`)
  }

  useImperativeHandle(ref, () => ({
    onSubmit: handleSubmit,
    onNext: () => true,
  }))

  return (
    <StyledOthers>
      <DatepickerInput
        title="Availability to Start"
        value={startAvailability}
        onChange={(date) => setStartAvailability(date)}
        dateFormat="MMMM dd yyyy"
        placeholderText="MM/YYYY"
        wrapperClass={fieldErrors.startAvailability && 'error'}
        subText={fieldErrors.startAvailability}
      />

      <SingleSelect
        title="Preferred Shift"
        options={shiftList}
        selected={shiftPreference}
        placeholder="Select Shift"
        onChange={(e) => setShiftPreference(e)}
        wrapperClass={fieldErrors.shiftPreference && 'error'}
        subText={fieldErrors.shiftPreference}
      />

      <MultiSelect
        title="Languages"
        options={languageList}
        selected={language}
        placeholder="Select Language"
        onChange={(e) => setLanguage(e)}
        wrapperClass={fieldErrors.language && 'error'}
        subText={fieldErrors.language}
      />

      <SingleSelect
        title="Level of Education"
        options={educationList}
        selected={education}
        placeholder="Select Level of Education"
        onChange={(e) => setEducation(e)}
        wrapperClass={fieldErrors.education && 'error'}
        subText={fieldErrors.education}
      />

      <StandardInput
        title="License/Certification"
        value={licenseAndCert}
        onChange={(value) => setLicenseAndCert(value)}
        wrapperClass={fieldErrors.licenseAndCert && 'error'}
        subText={fieldErrors.licenseAndCert}
      />

      <MultiSelect
        title="Software"
        options={softwareList}
        selected={software}
        placeholder="Select Software"
        onChange={(e) => setSoftware(e)}
        wrapperClass={fieldErrors.software && 'error'}
        subText={fieldErrors.software}
      />
    </StyledOthers>
  )
})

const StyledOthers = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  width: 100%;
`
Others.displayName = 'Others'

export default Others
